<template>
  <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
    <div class="card-header bg-secondary text-white">
      <h6 class="mb-0"><i class="fa fa-shopping-cart me-2"></i>Products</h6>
    </div>
    <div class="card-body">
      <div v-if="editable" class="d-flex justify-content-end mb-3">
        <button class="btn btn-sm btn-outline-primary" @click="addProduct">
          <i class="fa fa-plus me-1"></i>Add Product
        </button>
      </div>
      <!-- Existing products -->
      <div v-if="localProducts.length === 0" class="text-muted small">
        No products on this order.
      </div>

      <div
        v-for="(product, idx) in localProducts"
        :key="idx"
        class="border rounded p-3 mb-2"
        :class="
          product._operation === 'remove'
            ? 'bg-danger bg-opacity-10 border-danger'
            : product._operation === 'add'
              ? 'bg-success bg-opacity-10 border-success'
              : 'bg-light'
        "
      >
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div>
            <span class="badge me-1" :class="operationBadgeClass(rowOperation(product))">
              {{ rowOperation(product) || 'existing' }}
            </span>
            <strong v-if="!editable">{{ product.name || product.type }}</strong>
            <input
              v-else
              v-model="product.name"
              type="text"
              class="form-control border border-primary form-control-sm d-inline-block"
              style="width: 200px"
              placeholder="Product name"
            />
          </div>
          <button v-if="editable" class="btn btn-sm btn-outline-danger" @click="removeProduct(idx)">
            <i class="fa fa-trash"></i>
          </button>
        </div>

        <div class="row g-2">
          <div class="col-md-2">
            <label class="form-label small mb-1">Type</label>
            <select
              v-if="editable"
              v-model="product.type"
              class="form-select border border-primary form-select-sm"
              @change="product.unit = defaultUnitForType(product.type)"
            >
              <option value="">Select...</option>
              <option v-for="pt in productTypes" :key="pt" :value="pt">{{ pt }}</option>
            </select>
            <span v-else class="small">{{ product.type || '—' }}</span>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Quantity</label>
            <input
              v-if="editable"
              v-model.number="product.quantity"
              type="number"
              min="0"
              step="0.001"
              class="form-control border border-primary form-control-sm"
            />
            <span v-else class="small">{{ product.quantity ?? '—' }}</span>
          </div>
          <div class="col-md-1">
            <label class="form-label small mb-1">Unit</label>
            <select
              v-if="editable"
              v-model="product.unit"
              class="form-select border border-primary form-select-sm"
            >
              <option v-for="unit in units" :key="unit" :value="unit">{{ unit }}</option>
            </select>
            <span v-else class="small">{{ product.unit || '—' }}</span>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Unit Price</label>
            <input
              v-if="editable"
              v-model.number="product.unitPriceAmount"
              type="number"
              min="0"
              step="0.001"
              class="form-control border border-primary form-control-sm"
            />
            <span v-else class="small">{{
              formatCurrency(product.unitPrice?.amount, product.unitPrice?.currency)
            }}</span>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Total Price</label>
            <input
              v-if="editable"
              v-model.number="product.totalPriceAmount"
              type="number"
              min="0"
              step="0.01"
              class="form-control border border-primary form-control-sm"
            />
            <span v-else class="small">{{
              formatCurrency(product.totalPrice?.amount, product.totalPrice?.currency)
            }}</span>
          </div>
          <div class="col-md-1">
            <label class="form-label small mb-1">Currency</label>
            <select
              v-if="editable"
              v-model="product.currency"
              class="form-select border border-primary form-select-sm"
            >
              <option v-for="code in currencies" :key="code" :value="code">{{ code }}</option>
            </select>
            <span v-else class="small">{{ product.totalPrice?.currency || '—' }}</span>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Tax Class</label>
            <input
              v-if="editable"
              v-model.number="product.taxClass"
              type="number"
              class="form-control border border-primary form-control-sm"
              placeholder="19"
            />
            <span v-else class="small">{{ product.taxClass ?? '—' }}</span>
          </div>
        </div>

        <!-- Computed total check -->
        <div
          v-if="editable && product.quantity && product.unitPriceAmount != null"
          class="mt-1 small text-muted"
        >
          Computed total: {{ (product.quantity * product.unitPriceAmount).toFixed(2) }}
          <span
            v-if="
              product.totalPriceAmount != null &&
              Math.abs(product.totalPriceAmount - product.quantity * product.unitPriceAmount) > 0.01
            "
            class="text-warning ms-1"
          >
            (differs from entered total)
          </span>
        </div>
      </div>

      <!-- Totals summary -->
      <div v-if="localProducts.length > 0" class="border-top pt-2 mt-2 d-flex justify-content-end">
        <strong class="me-3"
          >Net total: {{ formatCurrency(computedNetTotal, defaultCurrency) }}</strong
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { formatCurrency } from '@/utils/display-formatters';
import { CURRENCIES } from '@/services/ryd-pay-operations/order-amendments/currency-constants';
import {
  ORDER_PRODUCT_TYPES,
  PRODUCT_UNITS,
  defaultUnitForType,
  isRowChanged,
  newEditableProduct,
  toEditableProduct,
} from '@/services/ryd-pay-operations/order-amendments/product-amendments';

const currencies = CURRENCIES;
const units = PRODUCT_UNITS;

const props = defineProps({
  products: { type: Array, default: () => [] },
  editable: { type: Boolean, default: false },
  /** order-service `ProductType` values; the backend rejects anything else. */
  productTypes: {
    type: Array,
    default: () => [...ORDER_PRODUCT_TYPES],
  },
  defaultCurrency: { type: String, default: 'EUR' },
  /** Tax class pre-filled on a new row (the order's country decides the real one). */
  defaultTaxClass: { type: Number, default: 19 },
});

const emit = defineEmits(['update:products']);

const localProducts = ref([]);

function initProducts() {
  // Rows are built from the order-service product shape (productType, paymentAttributes.*);
  // the emitted rows are what buildProductAmendments turns into the amendment payload.
  localProducts.value = (props.products || []).map((p) =>
    toEditableProduct(p, props.defaultCurrency),
  );
}

initProducts();
watch(() => props.products, initProducts, { deep: true });
watch(
  localProducts,
  (val) => {
    emit('update:products', val);
  },
  { deep: true },
);

function addProduct() {
  localProducts.value.push(newEditableProduct(props.defaultCurrency, props.defaultTaxClass));
}

function rowOperation(product) {
  if (product._operation) return product._operation;
  return isRowChanged(product) ? 'update' : null;
}

function removeProduct(idx) {
  const product = localProducts.value[idx];
  if (product._operation === 'add') localProducts.value.splice(idx, 1);
  else product._operation = 'remove';
}

const computedNetTotal = computed(() =>
  localProducts.value
    .filter((p) => p._operation !== 'remove')
    .reduce((sum, p) => {
      const total = p.totalPriceAmount ?? p.quantity * (p.unitPriceAmount || 0);
      return sum + (total || 0);
    }, 0),
);

function operationBadgeClass(op) {
  switch (op) {
    case 'add':
      return 'bg-success text-white';
    case 'update':
      return 'bg-warning text-dark';
    case 'remove':
      return 'bg-danger text-white';
    default:
      return 'bg-secondary text-white';
  }
}
</script>
